import { useApolloClient } from '@apollo/client'
import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import {
  PlanningBalancesDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { get12HFormat } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import AddShiftFromLeaveContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftFromLeaveContextualButton'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { trackEditTimeOffFromShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import type { Leave, ShiftsRow } from 'modules/shift_management/lib/types'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'

import BaseCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/BaseCell'
import LeaveModal from './LeaveModal'
import { styles } from './index.styles'
import './index.styles.css'

interface Props {
  leave: Leave
  shiftsRow: ShiftsRow
  date: moment.Moment
  span?: number
}

const tooltipContent = (leave: Leave, isTimeFormat24H: boolean): string => {
  if (leave.halfDay) {
    if (leave.halfDay === HALF_DAY_TYPES.beggining_of_day) {
      return i18n.t('time_off.half_day_options.first_half')
    }

    return i18n.t('time_off.half_day_options.second_half')
  }

  if (leave.hourly) {
    return i18n.tp('time_off.tooltip_leave_hours', {
      count: leave.hours,
      startTime: isTimeFormat24H ? leave.startTime : get12HFormat(leave.startTime),
    })
  }

  return i18n.tp('time_off.tooltip_leave_days', {
    count: leave.duration(),
    leaveTypeName: leave.typeName,
    startOn: leave.getStartOn().format('Do'),
  })
}

const getTypeTitle = (leave: Leave): string => {
  if (!leave.approved) return `${leave.typeName}`
  if (leave.halfDay) return i18n.t('shift_management.cells.half_absence.title')
  if (leave.hourly) return i18n.t('shift_management.cells.hourly_absence.title')

  return i18n.t('shift_management.cells.absence.title')
}

const LeaveItem = ({ leave, shiftsRow, date, span }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [isHovering, toggleHovering] = useToggle(false)
  const { isTimeFormat24H } = useIsTimeFormat24H()
  const hasBackupShifts = hasFeatureFlag('shift_management_backup_shifts')
  const [{ view }] = useSchedule()
  const typeTitle = getTypeTitle(leave)
  const isApproved = !!leave.approved
  const canAddBackupShifts = leave.canHaveBackupShifts
  const { viewOptions } = useCurrentViewOptions()
  const {
    schedule: { showCompactView },
  } = viewOptions
  const apolloClient = useApolloClient()

  const pendingTimeOffTitle = i18n.t('shift_management.cells.pending_time_off.title')
  const fullLabel = isApproved
    ? leave.halfDay
      ? `½ ${leave.typeName}`
      : leave.typeName
    : pendingTimeOffTitle

  const dispatchSelection = useSelectionDispatch()
  const { leave: selectedLeave } = useSelectionState()
  const isSelected = leave.id === selectedLeave?.id
  const borderColor = isSelected ? 'grey800' : 'champagne200'

  const { canManage, canApproveLeaves, canDeleteLeaves } = shiftsRow
  const canManageLeaves = canApproveLeaves || canDeleteLeaves

  const onToggle = () => {
    dispatchSelection({
      type: 'TOGGLE_LEAVE',
      leave,
    })
  }
  const onOpenLeaveModal = () => {
    onToggle()
    trackEditTimeOffFromShiftStartEvent({ view, isApproved })
  }

  const onCloseLeaveModal = () => {
    // TODO: Actually update the cache properly
    apolloClient.refetchQueries({
      include: [ShiftManagementMainPageDocument, PlanningBalancesDocument],
    })
    onToggle()
  }

  const employeeWithSelectedLeave = shiftsRow && isSelected

  // In weekly view we need to handle only rendering the modal once
  // in case leave have multiple days, especially with no backup shifts flag since
  // each day is handled separately and would cause multiple modals to be rendered
  const openLeaveModal = view.isWeekly
    ? employeeWithSelectedLeave && date.isSame(leave.getStartOn(), 'day')
    : employeeWithSelectedLeave

  return (
    <>
      <Box
        height={view.cellMaxHeight}
        maxHeight={showCompactView ? view.cellMinHeight : view.cellMaxHeight}
        minHeight={view.cellMinHeight}
        onMouseEnter={toggleHovering(true)}
        onMouseLeave={toggleHovering(false)}
        borderRadius={{ all: 'abs010' }}
        border={{ all: { color: borderColor, width: 's2', style: 'solid' } }}
        cursor={canManageLeaves ? 'pointer' : 'default'}
        onClick={canManageLeaves ? onOpenLeaveModal : null}
      >
        <BaseCell
          className={isApproved ? styles.approved : styles.unapproved}
          span={span}
          tooltip={
            <Box gapDivider gap="s16">
              <Box gap="s4">
                <Text.Header3 color="grey000White">{typeTitle}</Text.Header3>
                <Text.Normal size="200" weight="normal" color="grey000White">
                  {isApproved ? leave.typeName : ''}
                </Text.Normal>
              </Box>
              <Text.Normal size="200" weight="normal" color="grey000White">
                {tooltipContent(leave, isTimeFormat24H)}
              </Text.Normal>
            </Box>
          }
          body={`${typeTitle}. ${leave.getFormattedRange}`}
        >
          {fullLabel}
        </BaseCell>
        {hasBackupShifts && canManage && (
          <AddShiftFromLeaveContextualButton
            shiftsRow={shiftsRow}
            leave={leave}
            isBackup={canAddBackupShifts}
            date={date}
            isHoveringLeave={isHovering}
            toggleHovering={toggleHovering}
          />
        )}
      </Box>

      {openLeaveModal && (
        <LeaveModal leave={leave} onClose={onCloseLeaveModal} shiftsRow={shiftsRow} />
      )}
    </>
  )
}

export default observer(LeaveItem)
